import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  User, 
  Calendar, 
  Trophy, 
  Target, 
  TrendingUp,
  Settings,
  Edit3,
  Save,
  X
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProfileContainer = styled.div`
  min-height: 100vh;
  background: var(--color-background);
  padding: var(--spacing-xl) 0;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const ProfileHeader = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-2xl);
  margin-bottom: var(--spacing-xl);
  text-align: center;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-lg);
  color: white;
  font-size: 3rem;
  font-weight: 700;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarEmoji = styled.span`
  font-size: 3rem;
`;

const UserName = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
`;

const UserEmail = styled.p`
  color: var(--color-text-secondary);
  font-size: 1.125rem;
  margin-bottom: var(--spacing-lg);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
`;

const StatCard = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  text-align: center;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
  
  .stat-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto var(--spacing-md);
    padding: var(--spacing-md);
    border-radius: 50%;
    background: rgba(102, 126, 234, 0.1);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .stat-number {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-primary);
    margin-bottom: var(--spacing-xs);
  }
  
  .stat-label {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    
    svg {
      width: 20px;
      height: 20px;
      color: var(--color-primary);
    }
  }
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    background: var(--color-primary);
    color: white;
    border-color: var(--color-primary);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-lg);
  
  label {
    display: block;
    margin-bottom: var(--spacing-sm);
    font-weight: 500;
    color: var(--color-text-primary);
  }
  
  input {
    width: 100%;
    padding: var(--spacing-md);
    border: 2px solid var(--color-border);
    border-radius: var(--border-radius-md);
    font-size: 1rem;
    background: var(--color-background);
    color: var(--color-text-primary);
    transition: border-color var(--animation-duration) ease;
    
    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: var(--spacing-md);
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-md) var(--spacing-lg);
  border: 2px solid var(--color-primary);
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  
  &.primary {
    background: var(--color-primary);
    color: white;
    
    &:hover {
      background: var(--color-primary-dark);
    }
  }
  
  &.secondary {
    background: transparent;
    color: var(--color-primary);
    
    &:hover {
      background: var(--color-primary);
      color: white;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProgressItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) 0;
  border-bottom: 1px solid var(--color-border);
  
  &:last-child {
    border-bottom: none;
  }
  
  .dynasty-name {
    font-weight: 500;
    color: var(--color-text-primary);
  }
  
  .mastery-level {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    
    &.beginner {
      background: rgba(239, 68, 68, 0.1);
      color: #ef4444;
    }
    
    &.intermediate {
      background: rgba(245, 158, 11, 0.1);
      color: #f59e0b;
    }
    
    &.advanced {
      background: rgba(16, 185, 129, 0.1);
      color: #10b981;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Profile = () => {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || ''
  });

  // 获取用户统计
  const { data: userStats, isLoading: statsLoading } = useQuery(
    'user-stats',
    async () => {
      const response = await axios.get('/api/users/stats');
      return response.data.data;
    },
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // 获取用户掌握度
  const { data: userMastery, isLoading: masteryLoading } = useQuery(
    'user-mastery',
    async () => {
      const response = await axios.get('/api/quiz/user/mastery');
      return response.data.data;
    },
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // 更新用户信息
  const updateMutation = useMutation(
    async (data) => {
      const response = await axios.put('/api/auth/profile', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        updateUser(data.data);
        setIsEditing(false);
        toast.success('个人信息更新成功');
        queryClient.invalidateQueries('user-stats');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '更新失败');
      }
    }
  );

  const handleEdit = () => {
    setFormData({
      username: user?.username || '',
      email: user?.email || ''
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      username: user?.username || '',
      email: user?.email || ''
    });
  };

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getMasteryLevel = (score) => {
    if (score >= 80) return 'advanced';
    if (score >= 60) return 'intermediate';
    return 'beginner';
  };

  const getMasteryText = (score) => {
    if (score >= 80) return '精通';
    if (score >= 60) return '熟练';
    return '入门';
  };

  if (statsLoading || masteryLoading) {
    return (
      <ProfileContainer>
        <Container>
          <LoadingContainer>
            <LoadingSpinner />
            <p>正在加载个人信息...</p>
          </LoadingContainer>
        </Container>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <Container>
        <ProfileHeader>
          <Avatar>
            {user?.avatar && typeof user.avatar === 'string' && user.avatar.startsWith('emoji:') ? (
              <AvatarEmoji>{user.avatar.replace('emoji:', '')}</AvatarEmoji>
            ) : user?.avatar && typeof user.avatar === 'string' && !user.avatar.startsWith('emoji:') ? (
              <AvatarImage src={user.avatar} alt="avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <span>{user?.username?.charAt(0)?.toUpperCase() || 'U'}</span>
            )}
          </Avatar>
          <UserName>{user?.username}</UserName>
          {(!user?.isGuest && user?.email) && (
            <UserEmail>{user.email}</UserEmail>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', color: 'var(--color-text-muted)' }}>
            <Calendar size={16} />
            <span>
              加入时间: {(() => {
                const d = user?.createdAt ? new Date(user.createdAt) : null;
                const isValid = d && !isNaN(d.getTime());
                const fallback = user?.lastLogin ? new Date(user.lastLogin) : null;
                const dateToShow = isValid ? d : (fallback && !isNaN(fallback.getTime()) ? fallback : null);
                return dateToShow ? dateToShow.toLocaleDateString() : '未知';
              })()}
            </span>
          </div>
        </ProfileHeader>

        {userStats && (
          <StatsGrid>
            <StatCard>
              <div className="stat-icon">
                <Trophy />
              </div>
              <div className="stat-number">{userStats.stats.totalQuizzes}</div>
              <div className="stat-label">已完成测验</div>
            </StatCard>
            
            <StatCard>
              <div className="stat-icon">
                <Target />
              </div>
              <div className="stat-number">{userStats.stats.accuracy}%</div>
              <div className="stat-label">正确率</div>
            </StatCard>
            
            <StatCard>
              <div className="stat-icon">
                <TrendingUp />
              </div>
              <div className="stat-number">{userStats.stats.studyStreak}</div>
              <div className="stat-label">连续学习天数</div>
            </StatCard>
            
            <StatCard>
              <div className="stat-icon">
                <User />
              </div>
              <div className="stat-number">{userStats.progress.dynastyProgress.length}</div>
              <div className="stat-label">已学习朝代</div>
            </StatCard>
          </StatsGrid>
        )}

        <ContentGrid>
          <Section>
            <SectionHeader>
              <h2>
                <Settings />
                个人信息
              </h2>
              {!isEditing && (
                <EditButton onClick={handleEdit}>
                  <Edit3 />
                  编辑
                </EditButton>
              )}
            </SectionHeader>
            
            {isEditing ? (
              <>
                <FormGroup>
                  <label>用户名</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                
                {!user?.isGuest && (
                  <FormGroup>
                    <label>邮箱</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                )}
                
                <FormActions>
                  <ActionButton className="secondary" onClick={handleCancel}>
                    <X />
                    取消
                  </ActionButton>
                  <ActionButton 
                    className="primary" 
                    onClick={handleSave}
                    disabled={updateMutation.isLoading}
                  >
                    <Save />
                    {updateMutation.isLoading ? '保存中...' : '保存'}
                  </ActionButton>
                </FormActions>
              </>
            ) : (
              <>
                <FormGroup>
                  <label>用户名</label>
                  <input type="text" value={user?.username || ''} readOnly />
                </FormGroup>
                
                {!user?.isGuest && (
                  <FormGroup>
                    <label>邮箱</label>
                    <input type="email" value={user?.email || ''} readOnly />
                  </FormGroup>
                )}
              </>
            )}
          </Section>

          <Section>
            <SectionHeader>
              <h2>
                <TrendingUp />
                学习进度
              </h2>
            </SectionHeader>
            
            {userMastery && userMastery.length > 0 ? (
              userMastery.map((item) => (
                <ProgressItem key={item.dynasty._id}>
                  <span className="dynasty-name">{item.dynasty.name}</span>
                  <span className={`mastery-level ${getMasteryLevel(item.averageScore)}`}>
                    {getMasteryText(item.averageScore)}
                  </span>
                </ProgressItem>
              ))
            ) : (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem' }}>
                还没有学习记录，快去参加测验吧！
              </p>
            )}
          </Section>
        </ContentGrid>
      </Container>
    </ProfileContainer>
  );
};

export default Profile;
